import React, { useState, useEffect } from 'react';
import { 
  Folder, FolderOpen, File, Upload, Download, Search, 
  Trash2, Edit2, FileText, FolderPlus, Eye
} from 'lucide-react';
import api from '../services/api'; 
import { Toast } from '../components/ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import FilePreviewModal from './FilePreviewModal';
import Loader from './ui/Loader';
import { useLoader } from '../contexts/LoaderContext';

const DocumentsRH = () => {
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');


  const { showLoader, hideLoader } = useLoader();
  
  const [dossiers, setDossiers] = useState([]);
  const [fichiers, setFichiers] = useState([]);
  const [selectedDossier, setSelectedDossier] = useState(null);
  
  const [showCreateDossierModal, setShowCreateDossierModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditDossierModal, setShowEditDossierModal] = useState(false);
  
  // Preview
  const [previewFichier, setPreviewFichier] = useState(null);
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', description: '', type: 'warning', onConfirm: null, isLoading: false
  });
  
  const [nomDossier, setNomDossier] = useState('');
  const [descriptionDossier, setDescriptionDossier] = useState('');
  const [couleurDossier, setCouleurDossier] = useState('#448696');
  const [dossierToEdit, setDossierToEdit] = useState(null);
  
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTags, setUploadTags] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  useEffect(() => {
    loadDossiers();
    loadFichiers();
  }, []);

  useEffect(() => {
    loadFichiers();
  }, [selectedDossier, searchTerm]);

  const loadDossiers = async () => {
    try {
      const response = await api.documents.getDossiers();
      if (response.success) setDossiers(response.data);
    } catch (error) {
      console.error('Erreur chargement dossiers:', error);
    }
  };

  const loadFichiers = async () => {
    showLoader();
    setLoading(true);
    try {
      const response = await api.documents.getFichiers(selectedDossier?.id, searchTerm || null);
      if (response.success) setFichiers(response.data);
    } catch (error) {
      console.error('Erreur chargement fichiers:', error);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleCreateDossier = async () => {
    if (!nomDossier.trim()) { showToast('Veuillez saisir un nom de dossier', 'error'); return; }
    try {
      const response = await api.documents.createDossier({ nom: nomDossier, description: descriptionDossier, couleur: couleurDossier });
      if (response.success) {
        showToast('Dossier créé avec succès', 'success');
        setShowCreateDossierModal(false);
        setNomDossier(''); setDescriptionDossier(''); setCouleurDossier('#448696');
        loadDossiers();
      } else {
        showToast(response.message || 'Erreur lors de la création', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la création du dossier', 'error');
    }
  };

  const handleEditDossier = async () => {
    if (!nomDossier.trim()) { showToast('Veuillez saisir un nom de dossier', 'error'); return; }
    try {
      const response = await api.documents.updateDossier(dossierToEdit.id, { nom: nomDossier, description: descriptionDossier, couleur: couleurDossier });
      if (response.success) {
        showToast('Dossier modifié avec succès', 'success');
        setShowEditDossierModal(false); setDossierToEdit(null);
        setNomDossier(''); setDescriptionDossier(''); setCouleurDossier('#448696');
        loadDossiers();
      } else {
        showToast(response.message || 'Erreur lors de la modification', 'error');
      }
    } catch (error) {
      showToast('Erreur lors de la modification', 'error');
    }
  };

  const handleDeleteDossier = (dossier) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer le dossier',
      description: `Êtes-vous sûr de vouloir supprimer le dossier "${dossier.nom}" ? Cette action est irréversible.`,
      type: 'error',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          const response = await api.documents.deleteDossier(dossier.id);
          if (response.success) {
            showToast('Dossier supprimé avec succès', 'success');
            if (selectedDossier?.id === dossier.id) setSelectedDossier(null);
            loadDossiers();
            setConfirmDialog({ isOpen: false, title: '', description: '', type: 'warning', onConfirm: null, isLoading: false });
          } else {
            showToast(response.message || 'Erreur lors de la suppression', 'error');
            setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          }
        } catch (error) {
          showToast('Erreur lors de la suppression', 'error');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false
    });
  };

  const handleUploadFile = async () => {
    if (!uploadFile) { showToast('Veuillez sélectionner un fichier', 'error'); return; }
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (selectedDossier) formData.append('dossier_id', selectedDossier.id);
    formData.append('description', uploadDescription);
    formData.append('tags', uploadTags);
    try {
      const response = await api.documents.uploadFichier(formData);
      if (response.success) {
        showToast('Fichier uploadé avec succès', 'success');
        setShowUploadModal(false); setUploadFile(null); setUploadDescription(''); setUploadTags('');
        loadFichiers(); loadDossiers();
      } else {
        showToast(response.message || "Erreur lors de l'upload", 'error');
      }
    } catch (error) {
      showToast("Erreur lors de l'upload du fichier", 'error');
    }
  };

 const handleDownloadFile = async (fichier) => {
    try {
      showToast('Téléchargement en cours...', 'loading');
      const result = await api.documents.downloadFichier(fichier.id, fichier.nom_original);
      if (result.success) {
        showToast('Fichier téléchargé', 'success');
      } else {
        showToast('Erreur lors du téléchargement', 'error');
      }
    } catch (error) {
      showToast('Erreur lors du téléchargement', 'error');
    }
  };

  const handlePreviewFile = (fichier) => {
    setPreviewFichier(fichier);
  };

  const handleDeleteFile = (fichier) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer le fichier',
      description: `Êtes-vous sûr de vouloir supprimer "${fichier.nom_original}" ? Cette action est irréversible.`,
      type: 'error',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isLoading: true }));
        try {
          const response = await api.documents.deleteFichier(fichier.id);
          if (response.success) {
            showToast('Fichier supprimé avec succès', 'success');
            loadFichiers(); loadDossiers();
            setConfirmDialog({ isOpen: false, title: '', description: '', type: 'warning', onConfirm: null, isLoading: false });
          } else {
            showToast(response.message || 'Erreur lors de la suppression', 'error');
            setConfirmDialog(prev => ({ ...prev, isLoading: false }));
          }
        } catch (error) {
          showToast('Erreur lors de la suppression', 'error');
          setConfirmDialog(prev => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false
    });
  };

  const openEditModal = (dossier) => {
    setDossierToEdit(dossier);
    setNomDossier(dossier.nom);
    setDescriptionDossier(dossier.description || '');
    setCouleurDossier(dossier.couleur || '#448696');
    setShowEditDossierModal(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getFileIcon = (typeMime) => {
    if (typeMime?.includes('pdf')) return '📄';
    if (typeMime?.includes('word')) return '📝';
    if (typeMime?.includes('excel') || typeMime?.includes('spreadsheet')) return '📊';
    if (typeMime?.includes('image')) return '🖼️';
    return '📁';
  };

  const canPreview = (typeMime) => {
    if (!typeMime) return false;
    return typeMime === 'application/pdf' || typeMime.startsWith('image/') || typeMime === 'text/plain';
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      <Loader visible={loading} />
      
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ color: "#448696" }}>Base documentaire</h1>
        <p className="text-gray-600 text-base font-medium">Classement, partage et gestion des documents</p>
      </div>

      {/* Layout principal */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* SIDEBAR - Dossiers */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
            <FolderOpen size={24} className="text-gray-900" />
            <h2 className="font-bold text-gray-900 text-lg">Dossiers</h2>
          </div>

          <button
            onClick={() => setShowCreateDossierModal(true)}
            className="w-full py-3 px-4 text-gray-900 rounded-xl font-semibold text-sm mb-4 transition-all shadow-sm hover:shadow-md"
            style={{ backgroundColor: '#fed774' }}
          >
            <FolderPlus className="w-4 h-4 inline mr-2" />
            Nouveau dossier
          </button>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            <button
              onClick={() => setSelectedDossier(null)}
              className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                !selectedDossier ? 'bg-yellow-50 border-[#fed774] shadow-sm' : 'bg-white border-transparent hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5" style={{ color: '#fed774' }} />
                <div className="flex-1">
                  <div className="font-bold text-sm">Tous les fichiers</div>
                  <div className="text-xs font-medium text-gray-600">{fichiers.length} fichier(s)</div>
                </div>
              </div>
            </button>

            {dossiers.map((dossier) => (
              <div key={dossier.id} className="relative group">
                <button
                  onClick={() => setSelectedDossier(dossier)}
                  className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                    selectedDossier?.id === dossier.id ? 'bg-yellow-50 border-[#fed774] shadow-sm' : 'bg-white border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Folder className="w-5 h-5" style={{ color: dossier.couleur || '#448696' }} />
                    <div className="flex-1">
                      <div className="font-bold text-sm">{dossier.nom}</div>
                      <div className="text-xs font-medium text-gray-600">{dossier.nombre_fichiers || 0} fichier(s)</div>
                    </div>
                  </div>
                </button>
                <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); openEditModal(dossier); }} className="p-1.5 rounded-lg bg-white shadow-sm hover:bg-blue-50 transition-colors">
                    <Edit2 className="w-3.5 h-3.5" style={{ color: '#448696' }} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteDossier(dossier); }} className="p-1.5 rounded-lg bg-white shadow-sm hover:bg-red-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" style={{ color: '#fdac84' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CONTENU PRINCIPAL - Fichiers */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-9">
          
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center justify-between" style={{ backgroundColor: '#448696' }}>
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-white" />
              <h2 className="font-bold text-white text-lg">{selectedDossier ? selectedDossier.nom : 'Tous les fichiers'}</h2>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 text-xs rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              style={{ backgroundColor: '#fed774', color: '#1f2937' }}
            >
              <Upload className="w-4 h-4 inline mr-1" />
              Insérer
            </button>
          </div>

          {/* Recherche */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher un fichier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:outline-none font-medium text-sm transition-all"
              />
            </div>
          </div>

          {/* Liste des fichiers */}

          {fichiers.length === 0 && !loading ? (

            
            <div className="text-center py-12 text-gray-400">
              <File className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-sm font-medium">{searchTerm ? 'Aucun fichier trouvé' : 'Aucun fichier dans ce dossier'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fichiers.map((fichier) => (
                <div 
                  key={fichier.id} 
                  className="rounded-xl border border-gray-100 p-4 hover:shadow-lg transition-all duration-200 group cursor-pointer"
                  onClick={() => handlePreviewFile(fichier)}
                >
                  {/* Header fichier */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-3xl">{getFileIcon(fichier.type_mime)}</div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canPreview(fichier.type_mime) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePreviewFile(fichier); }}
                          className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          title="Visualiser"
                        >
                          <Eye className="w-4 h-4" style={{ color: '#448696' }} />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadFile(fichier); }}
                        className="p-2 rounded-lg hover:bg-green-50 transition-colors"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" style={{ color: '#fed774' }} />  
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteFile(fichier); }}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" style={{ color: '#fdac84' }} />
                      </button>
                    </div>
                  </div>

                  {/* Nom fichier */}
                  <div className="font-bold text-sm text-gray-900 mb-2 truncate" title={fichier.nom_original}>
                    {fichier.nom_original}
                  </div>

                  {/* Badge prévisualisable */}
                  {canPreview(fichier.type_mime) && (
                    <div className="mb-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-lg" style={{ backgroundColor: '#e8f4f7', color: '#5a9aaa' }}>
                        <Eye size={10} />
                        Visualisable
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {fichier.description && (
                    <div className="text-xs font-medium text-gray-600 mb-3 line-clamp-2">{fichier.description}</div>
                  )}

                  {/* Infos */}
                  <div className="flex items-center justify-between text-xs font-medium text-gray-500">
                    <span>{formatFileSize(fichier.taille)}</span>
                    <span>{formatDate(fichier.created_at)}</span>
                  </div>

                  {/* Tags */}
                  {fichier.tags && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {fichier.tags.split(',').map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700">{tag.trim()}</span>
                      ))}
                    </div>
                  )}

                  {/* Dossier parent */}
                  {fichier.dossier_nom && (
                    <div className="mt-2 flex items-center gap-1 text-xs font-medium text-gray-500">
                      <Folder className="w-3 h-3" />
                      {fichier.dossier_nom}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MODAL PREVIEW */}
      {previewFichier && (
        <FilePreviewModal
          fichier={previewFichier}
          onClose={() => setPreviewFichier(null)}
          onDownload={handleDownloadFile}
        />
      )}

      {/* MODAL CRÉER DOSSIER */}
      {showCreateDossierModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
              <FolderPlus size={24} className="text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900">Nouveau dossier</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
              <input type="text" value={nomDossier} onChange={(e) => setNomDossier(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" placeholder="Ex: Contrats 2025" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={descriptionDossier} onChange={(e) => setDescriptionDossier(e.target.value)} rows="3" className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" placeholder="Description du dossier" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Couleur</label>
              <div className="flex gap-2">
                {['#448696', '#fed774', '#22c55e', '#ef4444', '#8b5cf6', '#f59e0b'].map(color => (
                  <button key={color} onClick={() => setCouleurDossier(color)} className={`w-10 h-10 rounded-lg transition-all ${couleurDossier === color ? 'ring-4 ring-gray-300 scale-110' : ''}`} style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowCreateDossierModal(false); setNomDossier(''); setDescriptionDossier(''); setCouleurDossier('#448696'); }} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={handleCreateDossier} className="flex-1 px-4 py-3 text-gray-900 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#fed774' }}>Créer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MODIFIER DOSSIER */}
      {showEditDossierModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Edit2 size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Modifier le dossier</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
              <input type="text" value={nomDossier} onChange={(e) => setNomDossier(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={descriptionDossier} onChange={(e) => setDescriptionDossier(e.target.value)} rows="3" className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Couleur</label>
              <div className="flex gap-2">
                {['#448696', '#fed774', '#22c55e', '#ef4444', '#8b5cf6', '#f59e0b'].map(color => (
                  <button key={color} onClick={() => setCouleurDossier(color)} className={`w-10 h-10 rounded-lg transition-all ${couleurDossier === color ? 'ring-4 ring-gray-300 scale-110' : ''}`} style={{ backgroundColor: color }} />
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowEditDossierModal(false); setDossierToEdit(null); setNomDossier(''); setDescriptionDossier(''); setCouleurDossier('#448696'); }} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={handleEditDossier} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#448696' }}>Modifier</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL UPLOAD */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
              <Upload size={24} className="text-white" />
              <h3 className="text-xl font-bold text-white">Insérer un fichier</h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fichier *</label>
              <input type="file" onChange={(e) => setUploadFile(e.target.files[0])} className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} rows="2" className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" placeholder="Description du fichier" />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tags (séparés par virgule)</label>
              <input type="text" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} placeholder="contrat, 2025, important" className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none" />
            </div>
            {selectedDossier && (
              <div className="mb-4 p-3 bg-yellow-50 rounded-xl border border-[#fed774]">
                <div className="text-xs font-semibold text-gray-600 mb-1">Dossier de destination</div>
                <div className="flex items-center gap-2">
                  <Folder className="w-4 h-4" style={{ color: selectedDossier.couleur }} />
                  <span className="font-bold text-sm">{selectedDossier.nom}</span>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadDescription(''); setUploadTags(''); }} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50">Annuler</button>
              <button onClick={handleUploadFile} className="flex-1 px-4 py-3 text-white rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all" style={{ backgroundColor: '#448696' }}>
                <Upload className="w-4 h-4 inline mr-1" />
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ConfirmDialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, title: '', description: '', type: 'warning', onConfirm: null, isLoading: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        type={confirmDialog.type}
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={confirmDialog.isLoading}
      />

      {/* Toast */}
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}
    </div>
  );
};

export default DocumentsRH;